/**
 * School Admin Overview - Dashboard Summary
 *
 * Displays key metrics for school administrators:
 * - License utilization
 * - Student engagement
 * - Teacher adoption
 * - Learning time
 * - ROI indicators
 */

import React from 'react';

// Official Tappy Brand Colors
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkSlate: '#3D4954',
    mauve: '#8D7F88',
    mintGray: '#ABCACA',
    lightGray: '#D5DEDD',
    paleGray: '#ECF3F2',
    offWhite: '#F5F9FA',
    nearWhite: '#FCFDFE',
  }
};

function SchoolAdminOverview({ data, onRefresh, onNavigateToTab, onOpenExport }) {
  if (!data) {
    return <div>Loading overview...</div>;
  }

  const { school = {}, metrics = {}, teachers = {}, learning_time = {}, learning_independence = {}, roi = {} } = data;

  // Metric card component - iOS-style with Tappy colors
  const MetricCard = ({ value, label, sublabel, percentage, trend, alert }) => (
    <div style={{
      backgroundColor: 'white',
      padding: '28px',
      borderRadius: '16px',
      border: `2px solid ${TappyBrandColors.neutral.lightGray}`,
      boxShadow: '0 1px 3px rgba(26, 62, 72, 0.08)',
      transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
      cursor: 'default'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 8px 16px rgba(26, 62, 72, 0.12)';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = '0 1px 3px rgba(26, 62, 72, 0.08)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
    >
      <div style={{
        fontSize: '36px',
        fontWeight: 700,
        color: alert ? TappyBrandColors.accent.coral : TappyBrandColors.primary.teal,
        marginBottom: '10px'
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '13px',
        fontWeight: 600,
        color: TappyBrandColors.primary.brandBlue,
        marginBottom: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {label}
      </div>
      {sublabel && (
        <div style={{ fontSize: '12px', color: TappyBrandColors.neutral.mauve }}>
          {sublabel}
        </div>
      )}
      {percentage !== undefined && (
        <div style={{
          marginTop: '12px',
          height: '4px',
          backgroundColor: '#e5e7eb',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${percentage}%`,
            backgroundColor: percentage >= 80 ? '#10b981' : percentage >= 50 ? '#f59e0b' : '#ef4444',
            transition: 'width 0.3s'
          }} />
        </div>
      )}
      {trend && (
        <div style={{
          marginTop: '8px',
          fontSize: '12px',
          color: trend === 'up' ? '#10b981' : trend === 'down' ? '#ef4444' : '#6b7280'
        }}>
          {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trend}
        </div>
      )}
    </div>
  );

  // Calculate utilization color
  const getUtilizationColor = (pct) => {
    if (pct >= 85) return '#10b981'; // Green - good utilization
    if (pct >= 60) return '#f59e0b'; // Orange - moderate
    return '#ef4444'; // Red - low utilization
  };

  // Calculate expiration alert
  const getExpirationAlert = (days) => {
    return days < 60; // Alert if less than 60 days
  };

  return (
    <div>
      {/* School Header */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{
          fontSize: '32px',
          fontWeight: 600,
          color: TappyBrandColors.primary.darkTeal,
          marginBottom: '12px',
          letterSpacing: '-0.5px'
        }}>
          {school?.name || 'School Dashboard'}
        </h2>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <span style={{
            display: 'inline-block',
            padding: '6px 14px',
            backgroundColor: 'white',
            color: school?.license_status === 'active' ? TappyBrandColors.primary.skyBlue : TappyBrandColors.accent.coral,
            border: school?.license_status === 'active' ? `1.5px solid ${TappyBrandColors.primary.skyBlue}` : `1.5px solid ${TappyBrandColors.accent.coral}`,
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600
          }}>
            {school?.license_status === 'active' ? 'Active' : 'Inactive'}
          </span>
          <span style={{ fontSize: '14px', color: TappyBrandColors.primary.brandBlue }}>
            District: {school?.district_id || 'N/A'}
          </span>
          <button
            onClick={onRefresh}
            style={{
              marginLeft: 'auto',
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#374151'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px',
        marginBottom: '40px'
      }}>
        <MetricCard
          value={`${metrics.seats_used || 0}/${metrics.seats_total || 0}`}
          label="Seats Used"
          sublabel={`${metrics.seats_available || 0} available`}
          percentage={metrics.utilization_pct || 0}
        />

        <MetricCard
          value={`${metrics.active_students_pct || 0}%`}
          label="Active This Month"
          sublabel={`${metrics.active_students || 0} students`}
          percentage={metrics.active_students_pct || 0}
        />

        <MetricCard
          value={`$${metrics.cost_per_student || 0}`}
          label="Cost Per Student"
          sublabel={`Annual: $${metrics.annual_cost?.toLocaleString() || '0'}`}
        />

        <MetricCard
          value={`${teachers.active || 0}/${teachers.total || 0}`}
          label="Teachers Using Tappy"
          sublabel="Adoption rate"
          percentage={teachers.adoption_pct || 0}
        />

        <MetricCard
          value={`${learning_time.total_hours?.toLocaleString() || 0} hrs`}
          label="Learning Time"
          sublabel={`${learning_time.avg_hours_per_student || 0} hrs per student`}
        />

        <MetricCard
          value={`${learning_independence.avg_score || 0}/100`}
          label="Independence Avg"
          percentage={learning_independence.avg_score || 0}
        />

        <MetricCard
          value={metrics.days_until_expiration || 0}
          label="Days Until Expiration"
          sublabel={
            roi.roi_multiple > 0
              ? `${roi.roi_multiple}x ROI · $${(roi.equivalent_tutoring_value || 0).toLocaleString()} value`
              : (metrics.expiration_date ? new Date(metrics.expiration_date).toLocaleDateString() : 'N/A')
          }
          alert={getExpirationAlert(metrics.days_until_expiration || 365)}
        />
      </div>

      {/* Quick Actions */}
      <div style={{
        backgroundColor: 'white',
        padding: '28px',
        borderRadius: '16px',
        border: `2px solid ${TappyBrandColors.neutral.lightGray}`,
        boxShadow: '0 1px 3px rgba(26, 62, 72, 0.08)'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 600,
          color: TappyBrandColors.primary.darkTeal,
          marginBottom: '20px',
          letterSpacing: '-0.3px'
        }}>
          Quick Actions
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => onNavigateToTab && onNavigateToTab('analytics')}
            style={{
              padding: '12px 24px',
              backgroundColor: TappyBrandColors.primary.teal,
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              boxShadow: `0 2px 8px rgba(41, 122, 146, 0.25)`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
            View Full Report
          </button>
          <button
            onClick={() => onOpenExport && onOpenExport()}
            style={{
              padding: '12px 24px',
              backgroundColor: TappyBrandColors.neutral.paleGray,
              color: TappyBrandColors.neutral.darkSlate,
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.lightGray;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
            }}>
            Export Data
          </button>
          <button
            onClick={() => onNavigateToTab && onNavigateToTab('safety')}
            style={{
              padding: '12px 24px',
              backgroundColor: TappyBrandColors.neutral.paleGray,
              color: TappyBrandColors.neutral.darkSlate,
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.lightGray;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = TappyBrandColors.neutral.paleGray;
            }}>
            Manage Notifications
          </button>
        </div>
      </div>

      {/* Alerts Section - Clean iOS-style */}
      {getExpirationAlert(metrics.days_until_expiration || 365) && (
        <div style={{
          marginTop: '24px',
          padding: '20px',
          backgroundColor: TappyBrandColors.neutral.paleGray,
          border: `2px solid ${TappyBrandColors.accent.gold}`,
          borderLeft: `4px solid ${TappyBrandColors.accent.gold}`,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: `0 4px 12px rgba(236, 201, 98, 0.15)`
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            backgroundColor: 'white',
            border: `2px solid ${TappyBrandColors.accent.gold}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 700,
              color: TappyBrandColors.accent.gold
            }}>!</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontWeight: 600,
              color: TappyBrandColors.neutral.darkSlate,
              marginBottom: '6px',
              fontSize: '15px'
            }}>
              License Expiring Soon
            </div>
            <div style={{
              fontSize: '13px',
              color: TappyBrandColors.primary.brandBlue,
              lineHeight: '1.5'
            }}>
              Your license expires in {metrics.days_until_expiration || 0} days. Contact your Tappy representative to renew.
            </div>
          </div>
          <button style={{
            padding: '10px 20px',
            backgroundColor: TappyBrandColors.accent.gold,
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            flexShrink: 0,
            transition: 'all 200ms ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = TappyBrandColors.accent.brightGold;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = TappyBrandColors.accent.gold;
          }}>
            Renew Now
          </button>
        </div>
      )}

      {(metrics.utilization_pct || 0) < 60 && (
        <div style={{
          marginTop: '24px',
          padding: '20px',
          backgroundColor: TappyBrandColors.neutral.paleGray,
          border: `2px solid ${TappyBrandColors.primary.brightTeal}`,
          borderLeft: `4px solid ${TappyBrandColors.primary.brightTeal}`,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: `0 4px 12px rgba(66, 144, 158, 0.15)`
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            backgroundColor: 'white',
            border: `2px solid ${TappyBrandColors.primary.brightTeal}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 600,
              color: TappyBrandColors.primary.brightTeal
            }}>i</div>
          </div>
          <div>
            <div style={{
              fontWeight: 600,
              color: TappyBrandColors.neutral.darkSlate,
              marginBottom: '6px',
              fontSize: '15px'
            }}>
              Low Seat Utilization
            </div>
            <div style={{
              fontSize: '13px',
              color: TappyBrandColors.primary.brandBlue,
              lineHeight: '1.5'
            }}>
              You're using {metrics.utilization_pct || 0}% of your seats. Consider reallocating unused seats or adjusting your license size.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SchoolAdminOverview;
